'use client'

import { QueryClient } from '@tanstack/react-query'
import { NotificationType } from '@/types/notification'
import { ModuleEnum } from '@/enums'
import {
  LocaleContentHandler,
  ProductsExcelExportContentHandler,
  ProductsQrExportContentHandler,
  ProductsPdfExportContentHandler,
  ProductsExcelImportContentHandler,
  ProductsPictureImportContentHandler,
  BackupsContentHandler,
  ContactFormMessagesContentHandler,
  JobApplicationsContentHandler,
  OrdersContentHandler,
  UsersContentHandler
} from '@/lib/handler/notification'

/**
 * Base handler interface for notification content updates
 */
export interface INotificationContentHandler {
  handle(notification: NotificationType, queryClient: QueryClient): void
}

/**
 * The default fallback handler
 * Does nothing - for modules that don't need content updates
 */
class DefaultContentHandler implements INotificationContentHandler {
  handle(_notification: NotificationType, _queryClient: QueryClient): void {
    // Do nothing
  }
}

/**
 * Factory class to get the appropriate handler based on the notification module
 */
class NotificationContentHandlerFactory {
  private static handlers: Map<string, INotificationContentHandler> = new Map([
    [ModuleEnum.LOCALES, new LocaleContentHandler()],
    [ModuleEnum.PRODUCTS_EXCEL_EXPORTS, new ProductsExcelExportContentHandler()],
    [ModuleEnum.PRODUCTS_QR_EXPORTS, new ProductsQrExportContentHandler()],
    [ModuleEnum.PRODUCTS_PDF_EXPORTS, new ProductsPdfExportContentHandler()],
    [ModuleEnum.PRODUCTS_EXCEL_IMPORTS, new ProductsExcelImportContentHandler()],
    [ModuleEnum.PRODUCTS_PICTURE_IMPORTS, new ProductsPictureImportContentHandler()],
    [ModuleEnum.BACKUPS, new BackupsContentHandler()],
    [ModuleEnum.CONTACT_FORM_MESSAGES, new ContactFormMessagesContentHandler()],
    [ModuleEnum.JOB_APPLICATIONS, new JobApplicationsContentHandler()],
    [ModuleEnum.ORDERS, new OrdersContentHandler()],
    [ModuleEnum.USERS, new UsersContentHandler()]
  ])

  private static defaultHandler = new DefaultContentHandler()

  static getHandler(module: string): INotificationContentHandler {
    return this.handlers.get(module) || this.defaultHandler
  }
}

/**
 * Main function to handle notification content updates
 * @param notification - The notification object from WebSocket
 * @param queryClient - React Query client instance
 */
export const handleNotificationContent = (notification: NotificationType, queryClient: QueryClient): void => {
  const handler = NotificationContentHandlerFactory.getHandler(notification.module)

  handler.handle(notification, queryClient)
}
